import type { StylePropertiesTypes } from '@wix/document-services-types'
import type { Spx } from '@wix/thunderbolt-becky-types'
import type { StylePropItem } from '@wix/thunderbolt-becky-root'
import { getConverter } from '../utils/convertersRegistry'
import { getStringFromConverter } from '../utils/converterHelpers'

/** Utility function to handle the repetitive prop conversion pattern */
const convertInternalStyleProp = (prop: StylePropItem | undefined, spx: Spx): string | undefined => {
	if (!prop) {
		return undefined
	}
	const converter = getConverter(prop.type)
	return converter ? getStringFromConverter(converter(prop, spx)) : undefined
}

/** Internal Converters */
const coordinatesConverter = (prop: StylePropertiesTypes.Coordinates<StylePropItem>, spx: Spx) => {
	const { top: topProp, right: rightProp, bottom: bottomProp, left: leftProp } = prop

	const top = convertInternalStyleProp(topProp, spx)
	const right = convertInternalStyleProp(rightProp, spx)
	const bottom = convertInternalStyleProp(bottomProp, spx)
	const left = convertInternalStyleProp(leftProp, spx)

	return {
		top,
		right,
		bottom,
		left,
		[prop.type]: 'var(--{propName}-top) var(--{propName}-right) var(--{propName}-bottom) var(--{propName}-left)',
	}
}

const dimensionsConverter = (prop: StylePropertiesTypes.Dimensions<StylePropItem>, spx: Spx) => {
	const { horizontal: horizontalProp, vertical: verticalProp } = prop

	const horizontal = convertInternalStyleProp(horizontalProp, spx)
	const vertical = convertInternalStyleProp(verticalProp, spx)

	return `${horizontal} ${vertical}`
}

const cornersConverter = (prop: StylePropertiesTypes.Corners<StylePropItem>, spx: Spx) => {
	const {
		topLeft: topLeftProp,
		topRight: topRightProp,
		bottomRight: bottomRightProp,
		bottomLeft: bottomLeftProp,
	} = prop

	const topLeft = convertInternalStyleProp(topLeftProp, spx)
	const topRight = convertInternalStyleProp(topRightProp, spx)
	const bottomRight = convertInternalStyleProp(bottomRightProp, spx)
	const bottomLeft = convertInternalStyleProp(bottomLeftProp, spx)

	return {
		topLeft,
		topRight,
		bottomRight,
		bottomLeft,
		[prop.type]:
			'var(--{propName}-topLeft) var(--{propName}-topRight) var(--{propName}-bottomRight) var(--{propName}-bottomLeft)',
	}
}

const isComplexList = (
	prop: StylePropertiesTypes.List<StylePropItem | string>
): prop is StylePropertiesTypes.List<StylePropItem> => typeof prop.value[0] !== 'string'

const listConverter = (prop: StylePropertiesTypes.List<StylePropItem | string>, spx?: Spx) => {
	if (isComplexList(prop)) {
		return prop.value
			.map((item) => {
				const converter = getConverter(item.type)
				const convertedStyleProp = converter?.(item, spx)

				if (typeof convertedStyleProp === 'string') {
					return convertedStyleProp
				}

				if (!convertedStyleProp || typeof convertedStyleProp !== 'object') {
					return ''
				}

				const mainStylePropArray = convertedStyleProp[item.type]?.split(' ').map((cssVar: string) => {
					if (cssVar.includes('var(--{propName}-')) {
						const key = cssVar.split('var(--{propName}-')[1].split(')')[0]
						return convertedStyleProp[key]
					}
					return cssVar
				})

				return mainStylePropArray?.filter(Boolean).join(' ') || ''
			})
			.join(', ')
	}

	return prop.value.map((item) => ((item as string).includes(' ') ? `'${item}'` : item)).join(', ')
}

/** External Map */
export const genericsConverterMap = {
	Coordinates: coordinatesConverter,
	Corners: cornersConverter,
	Dimensions: dimensionsConverter,
	List: listConverter,
}
